import ko from "knockout";
import _ from "lodash";
import ObservableModel from "util/ObservableModel";
import participantStatus from "enums/participantStatus";

const CallParticipant = ObservableModel.extend({
    defaults: {
        state: null,
        held: false,
        muted: false,
        recording: false,
        confined: false,
        errorInfo: null,
    },
});

const MessageParticipant = ObservableModel.extend({
    defaults: {
        state: null,
        held: false,
        messages: null,
        confined: false,
        errorInfo: null,
    },
});

const ScreenMonitoring = ObservableModel.extend({
    defaults: {
        id: null,
        initialState: null,
        state: null,
        disconnectType: null,
        provider: null,
        targetUser: null,
        screenMonitoring: null,
        monitoringType: null,
        count: null,
        connectedTime: null,
        disconnectedTime: null,
    },
});

const Participant = ObservableModel.extend(
    {
        defaults: {
            userUri: null,
            userId: null,
            user: null,
            address: null,
            name: null,
            purpose: null,
            monitoredParticipantId: null,
            coachedParticipantId: null,
            bargedParticipantId: null,
            isSupervisorBarged: false,
            consultParticipantId: null,
            startTime: null,
            endTime: null,
            state: null,
            connectedTime: null,
            wrapup: null,
            wrapupRequired: null,
            wrapupPrompt: null,
            wrapupExpected: null,
            wrapupSkipped: null,
            wrapupTimeoutMs: null,
            //TODO: this is a stopgap for the demo.
            attributes: {
                consultInitiator: false,
            },
        },
        modelProperties: {
            calls: CallParticipant,
            callbacks: CallParticipant,
            messages: MessageParticipant,
            screenMonitorings: ScreenMonitoring,
        },
    },
    function () {
        const self = this;

        self.userImageError = ko.computed(function () {
            return (
                !self.user() ||
                !self.user().userImageError ||
                self.user().userImageError()
            );
        });

        self.userImages = ko.computed(function () {
            if (!self.userImageError()) {
                return self.user().avatarImages();
            }
        });

        self.isVoicemail = ko.computed(function () {
            return self.purpose() === "voicemail";
        });

        self.isUser = ko.computed(function () {
            return self.purpose() === "user";
        });

        self.isAgent = ko.computed(function () {
            return self.purpose() === "agent";
        });

        self.isUserOrAgent = ko.computed(function () {
            return self.purpose() === "user" || self.purpose() === "agent";
        });

        // At some point in the future, it will be possible for a conversation to have multiple calls.
        // For now, there will just be one.  This provides a wrapper around getting the current call
        // so that we can inspect if it's being held/muted and it's status.
        self.currentCall = ko.computed(function () {
            const communications = self.calls || self.callbacks;
            return _.head(ko.unwrap(communications));
        });

        self.currentMessage = ko.computed(function () {
            const communications = self.messages;
            return _.head(ko.unwrap(communications));
        });

        self.currentScreenMonitoring = ko.computed(function () {
            const screenMonitorings = self.screenMonitorings;
            return _.head(ko.unwrap(screenMonitorings));
        });

        self.formattedNumber = ko.computed(function () {
            if (self.address()) {
                return self.address();
            }

            if (self.callbacks) {
                const currentCallback = _.head(ko.unwrap(self.callbacks));
                return (
                    currentCallback && _.head(currentCallback.callbackNumbers())
                );
            }
            return null;
        });

        self.status = ko.computed({
            read: function () {
                if (self.state()) {
                    return self.state().toUpperCase();
                } else if (self.currentCall() && self.currentCall().state()) {
                    return self.currentCall().state().toUpperCase();
                } else if (
                    self.currentMessage() &&
                    self.currentMessage().state()
                ) {
                    return self.currentMessage().state().toUpperCase();
                } else if (
                    self.currentScreenMonitoring() &&
                    self.currentScreenMonitoring().state()
                ) {
                    return self.currentScreenMonitoring().state().toUpperCase();
                }
                return null;
            },
            write: function (value) {
                if (self.currentCall()) {
                    self.currentCall().state(value.toUpperCase());
                } else if (self.currentMessage()) {
                    self.currentMessage().state(value.toUpperCase());
                } else if (self.currentScreenMonitoring()) {
                    self.currentScreenMonitoring().state(value.toUpperCase());
                }
            },
        });

        self.isDisconnected = ko.computed(function () {
            return (
                self.status() === participantStatus.DISCONNECTED ||
                self.status() === participantStatus.TERMINATED
            );
        });

        self.isContacting = ko.computed(function () {
            return (
                self.status() === participantStatus.CONTACTING ||
                self.status() === participantStatus.DIALING ||
                self.status() === participantStatus.ALERTING
            );
        });

        self.held = ko.computed({
            read: function () {
                if (self.currentCall()) {
                    return self.currentCall().held();
                } else if (self.currentMessage()) {
                    return self.currentMessage().held();
                }
                return null;
            },
            write: function (value) {
                if (self.currentCall()) {
                    self.currentCall().held(value);
                } else if (self.currentMessage()) {
                    self.currentMessage().held(value);
                }
            },
        });

        self.recording = ko.computed({
            read: function () {
                if (self.currentCall()) {
                    return self.currentCall().recording();
                }
                return null;
            },
            write: function (value) {
                if (self.currentCall()) {
                    self.currentCall().recording(value);
                }
            },
        });

        self.confined = ko.computed({
            read: function () {
                if (self.currentCall()) {
                    return self.currentCall().confined();
                }
                return null;
            },
            write: function (value) {
                if (self.currentCall()) {
                    self.currentCall().confined(value);
                }
            },
        });

        self.muted = ko.computed({
            read: function () {
                if (self.currentCall()) {
                    return self.currentCall().muted();
                }
                return null;
            },
            write: function (value) {
                if (self.currentCall()) {
                    self.currentCall().muted(value);
                }
            },
        });

        self.errorInformation = ko.computed(function () {
            if (self.currentCall() && self.currentCall().errorInfo()) {
                return self.currentCall().errorInfo();
            } else if (
                self.currentMessage() &&
                self.currentMessage().errorInfo()
            ) {
                return self.currentMessage().errorInfo();
            }
            return null;
        });

        ko.computed(function () {
            if (!self.userId()) {
                if (self.user()) {
                    self.userId(ko.unwrap(self.user().id));
                } else if (self.userUri()) {
                    const index = self.userUri().lastIndexOf("/");
                    self.userId(self.userUri().substring(index + 1));
                }
            }
        });
    }
);

export default Participant;
